import { createContext, useContext, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query"; // Import TanStack Query
import { getRequest } from "services/api";
import Swal from "sweetalert2";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { buildQueryString } from "utils/queryHelpers";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";

const DailyTransactionContext = createContext();

export const DailyTransactionProvider = ({ children }) => {
  const now = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(now.getDate() - 7);

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    filter: {
      start_date: now.toISOString().split("T")[0],
      end_date: now.toISOString().split("T")[0],
    },
  });

  // const [filterOptions, setFilterOptions] = useState({
  //   search: "",
  //   currentPage: 1,
  //   filter: {
  //     start_date: "",
  //     end_date: "",
  //   },
  // });

  const { start_date, end_date } = filterOptions.filter;

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    paginate: LIMIT_DATA_PER_PAGE,
    "filter[start_date]": start_date,
    "filter[end_date]": end_date,
    // include: "paymentMethod,transactionItems",
    // include:"paymentMethod,transactionItems,cashierSession,transactionItemsCount,transactionItemsExists",
    // "include":"paymentMethod,transactionItems",
  });

  // Fetch data menggunakan TanStack Query
  const {
    data: transactions,
    error,
    isLoading,
    refetch, // Untuk manual refresh data
  } = useQuery({
    queryKey: ["transactions", filterOptions],
    // queryFn: async () => {
    //   const response = await getRequest(`/transaction/report${queryString}`);
    //   if (!response.success) {
    //     throw new Error(response.message || "Failed to fetch transactions.");
    //   }
    //   return response;
    // },
    queryFn: async () => {
      const { start_date, end_date } = filterOptions.filter;

      if (!start_date || !end_date) {
        Swal.fire({
          icon: "warning",
          title: "Filter Wajib Diisi",
          text: "Harap pilih rentang tanggal (start_date dan end_date).",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });

        throw new Error("Start date and end date are required.");
      }

      const response = await getRequest(`/transaction/report${queryString}`);
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch transactions.");
      }
      return response;
    },

    keepPreviousData: true, // Agar tidak berkedip saat berpindah halaman
  });

  // daily summary
  // const summaryV1 = useMemo(() => {
  //   const itemsMap = new Map();

  //   transactions?.data?.forEach((tx) => {
  //     tx.transaction_items.forEach((item) => {
  //       const key = item.item_name;
  //       const current = itemsMap.get(key) || {
  //         item_name: key,
  //         quantity: 0,
  //         total: 0,
  //       };
  //       current.quantity += item.quantity;
  //       current.total += item.quantity * parseFloat(item.price);
  //       itemsMap.set(key, current);
  //     });
  //   });

  //   return Array.from(itemsMap.values());
  // }, [transactions]);

  // const summary = useMemo(() => {
  //   const itemsMap = new Map();

  //   transactions?.data?.forEach((tx) => {
  //     const items = tx.transaction_items || []; // ✅ fallback untuk undefined
  //     items.forEach((item) => {
  //       const key = item.item_name;
  //       const current = itemsMap.get(key) || {
  //         item_name: key,
  //         quantity: 0,
  //         total: 0,
  //       };
  //       current.quantity += item.quantity;
  //       current.total += item.quantity * parseFloat(item.price);
  //       itemsMap.set(key, current);
  //     });
  //   });

  //   return Array.from(itemsMap.values());
  // }, [transactions]);

  // Handle pencarian (Enter untuk submit)
  const handleSearch = (e) => {
    if (e.key === "Enter") {
      if (!e.target.value.trim()) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "warning",
          title: "Search input cannot be empty!",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }
      setFilterOptions((prev) => ({
        ...prev,
        search: e.target.value,
        currentPage: 1, // Reset ke halaman pertama setelah pencarian
      }));
    }
  };

  // DailyTransactionContext.js
  const setDateRange = useCallback((start, end) => {
    setFilterOptions((prev) => {
      if (prev.filter.start_date === start && prev.filter.end_date === end) {
        return prev;
      }
      return {
        ...prev,
        filter: {
          ...prev.filter,
          start_date: start,
          end_date: end,
        },
        currentPage: 1, // reset ke halaman awal saat ganti tanggal
      };
    });
  }, []);

  const handlePageChange = (page) => {
    setFilterOptions((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  const totalPages = transactions?.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <DailyTransactionContext.Provider
      value={{
        transactions: transactions?.data || [],
        summary: transactions?.data || [],
        summaryData: transactions?.summary || {},
        isLoading,
        error,
        filterOptions,
        handleSearch,
        refetchTransaction: refetch, // Bisa dipanggil untuk refresh data
        Pagination,
        setDateRange, // ✅ tambahkan ini
      }}
    >
      {children}
    </DailyTransactionContext.Provider>
  );
};

export const useDailyTransaction = () => {
  const context = useContext(DailyTransactionContext);
  if (!context) {
    throw new Error(
      "useDailyTransaction must be used within DailyTransactionProvider"
    );
  }
  return context;
};
